import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useStyles } from "./SplashScreen.style";
import { isAndroidApp, isAndroidAppTv, isTV } from "../../../utils/platform";
import { useAppSelector } from "../../hooks";
import { selectAuthReady, selectIsAuthenticated } from "@onmo/auth";

const SPLASH_SHOWN_KEY = "splash-screen-shown";
const SPLASH_DURATION = 3400;
const TRANSITION_HOLD_MS = 0;
const UNMOUNT_DELAY_MS = 50;
export const APP_READY_EVENT = "app-ready";

export const isSplashScreenEnabled = isTV || isAndroidApp;

const isNativeSplash = isAndroidAppTv || isAndroidApp;
const splashType: "native" | "mp4" = isNativeSplash ? "native" : "mp4";

/**
 * splash:     Video splash đang chạy, children chưa render
 * transition: Video đã pause ở frame cuối, children bắt đầu mount, (chờ 2x rAF + TRANSITION_HOLD_MS rồi sang ready)
 * ready:      Splash fade về opacity 0 (CSS transition 200ms)
 * hidden:     Splash unmount khỏi DOM (UNMOUNT_DELAY_MS ms sau khi vào ready)
 */
type SplashPhase = "splash" | "transition" | "ready" | "hidden";

const getInitialPhase = (): SplashPhase => {
  if (!isSplashScreenEnabled) return "hidden";
  if (splashType === "native") return "transition"; // Native splash holds last frame; JS drives dismiss
  if (sessionStorage.getItem(SPLASH_SHOWN_KEY)) return "hidden";
  return "splash";
};

/**
 * isAuthenticated = true
 * Listen for APP_READY_EVENT (auth flow: MainLayout signals when user data is loaded)
*/
const subscribeAppReady = (onReady: () => void): (() => void) => {
  window.addEventListener(APP_READY_EVENT, onReady);
  return () => window.removeEventListener(APP_READY_EVENT, onReady);
};

/**
 * isAuthenticated = false
 * Wait for first DOM mutation in #root + 1 paint frame (non-auth flow)
*/
const subscribeChildPaint = (onReady: () => void): (() => void) => {
  const root = document.getElementById("root");
  if (!root) {
    onReady();
    return () => {};
  }
  let raf = 0;
  const observer = new MutationObserver(() => {
    observer.disconnect();
    raf = window.requestAnimationFrame(onReady);
  });
  observer.observe(root, { childList: true, subtree: true });
  return () => {
    observer.disconnect();
    cancelAnimationFrame(raf);
  };
};

export const SplashScreen = ({ children }: { children: React.ReactNode }) => {
  const classes = useStyles();
  const videoRef = useRef<HTMLVideoElement>(null);
  const [phase, setPhase] = useState<SplashPhase>(getInitialPhase);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const authReady = useAppSelector(selectAuthReady);

  const hideSplash = useCallback(() => {
    sessionStorage.setItem(SPLASH_SHOWN_KEY, "1");
    setPhase("transition");
  }, []);

  useEffect(() => {
    if (!authReady) return;
    if (phase !== "transition") return;

    let timer = 0;
    const scheduleReady = () => {
      if (splashType === "native") {
        window.AndroidBridge?.dismissNativeSplash?.();
        setPhase("hidden");
        return;
      }
      timer = window.setTimeout(() => setPhase("ready"), TRANSITION_HOLD_MS);
    };

    const unsubscribe =
      isSplashScreenEnabled && isAuthenticated
        ? subscribeAppReady(scheduleReady)
        : subscribeChildPaint(scheduleReady);

    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [phase, isAuthenticated, authReady]);

  // Splash video playback
  useEffect(() => {
    if (phase !== "splash") return;

    const video = videoRef.current;
    if (!video) {
      hideSplash();
      return;
    }

    const tryPlay = () => {
      video.play().catch(hideSplash);
    };

    video.addEventListener("canplay", tryPlay);
    video.addEventListener("ended", hideSplash);
    video.addEventListener("error", hideSplash);

    const playbackDelay = window.setTimeout(tryPlay, 500);
    const timeout = window.setTimeout(hideSplash, SPLASH_DURATION + 2000);

    return () => {
      clearTimeout(playbackDelay);
      clearTimeout(timeout);
      video.removeEventListener("canplay", tryPlay);
      video.removeEventListener("ended", hideSplash);
      video.removeEventListener("error", hideSplash);
      video.pause(); // freeze on the last frame when leaving splash
    };
  }, [phase, hideSplash]);

  useEffect(() => {
    if (phase !== "ready") return;
    const unmount = window.setTimeout(() => setPhase("hidden"), UNMOUNT_DELAY_MS);
    return () => {
      clearTimeout(unmount);
    };
  }, [phase]);

  const showChildren = phase !== "splash";
  const showSplash = phase !== "hidden" && splashType !== "native";
  const splashOpacity = phase === "ready" ? 0 : 1;

  return (
    <Fragment>
      {showChildren && children}
      {showSplash && (
        <div
          className={classes.root}
          style={{ opacity: splashOpacity }}
        >
          <video
            ref={videoRef}
            className={classes.splash}
            src="./videos/splash-screen.mp4"
            autoPlay
            playsInline
            preload="auto"
          />
        </div>
      )}
    </Fragment>
  );
};
